import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
	AutoFocusDirective,
	FormComponent,
	InvalidTypeDirective,
	InputErrorComponent,
	InputComponent,
} from '@woolworthsnz/form';
import {
	ButtonComponent,
	ButtonState,
	DatalayerService,
	ModalOverlayService,
	NotificationType,
} from '@woolworthsnz/styleguide';
import { ProductService } from '../../services/product.service';
import { NgIf, NgTemplateOutlet } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'product-shopper-notes',
	template: `
		<ng-container *ngIf="isModal">
			<h5 i18n="@@shopperNotesTitle" id="dialog-title">{{ title }}</h5>
			<figure class="shopperNotes-figure" *ngIf="productImageSrc">
				<img [src]="productImageSrc" />
				<figcaption>{{ productName }}</figcaption>
			</figure>
		</ng-container>
		<p i18n="@@shopperNotesDescription" id="dialog-description">
			{{ description }}
		</p>
		<form
			[formGroup]="shopperNotesForm"
			[formAction]="shopperNotesEndpoint"
			cdxForm
			#form
			(formSubmitted)="onSubmit()"
		>
			<ng-container *ngIf="isModal; else nonFocusableInput">
				<form-input formAutoFocus formControlName="shopperNote" inputType="textarea" [maxlength]="maxlength">
					<ng-container label *ngTemplateOutlet="formLabel"></ng-container>
					<ng-container validation *ngTemplateOutlet="validationErrors"></ng-container>
				</form-input>
			</ng-container>
			<ng-template #nonFocusableInput>
				<form-input formControlName="shopperNote" inputType="textarea" [maxlength]="maxlength">
					<ng-container label *ngTemplateOutlet="formLabel"></ng-container>
					<ng-container validation *ngTemplateOutlet="validationErrors"></ng-container>
				</form-input>
			</ng-template>
			<ng-template #formLabel>
				<label i18n="@@shopperNotes-textareaLabel" for="shopperNote" label class="sr-only"
					>Shopper notes for {{ productName }}</label
				>
			</ng-template>
			<ng-template #validationErrors>
				<form-input-error control="shopperNote">
					<p
						attr.data-cy="shopperNote-error"
						cdxInvalidType="serverError"
						i18n="@@shopperNotes-postShopperNotesServerErrorMessage"
					>
						Something went wrong. For further assistance, please call 0800 40 40 40.
					</p>
					<p
						attr.data-cy="shopperNote-error"
						cdxInvalidType="invalidChars"
						i18n="@@shopperNotes-postShopperNotesInvalidCharsMessage"
					>
						Invalid characters in the shopper note.
					</p>
				</form-input-error>
			</ng-template>
			<button
				i18n="@@shopperNotes-submitButton"
				attr.aria-label="Shopper notes submit button"
				[fullWidth]="isModal"
				type="button"
				[buttonState]="buttonState"
				[disabled]="!canSubmit()"
				(click)="onSubmit()"
				cdxButton
			>
				Save notes
			</button>
		</form>
	`,
	styleUrls: ['./shopper-notes.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		FormsModule,
		FormComponent,
		ReactiveFormsModule,
		InputComponent,
		AutoFocusDirective,
		NgTemplateOutlet,
		InputErrorComponent,
		InvalidTypeDirective,
		ButtonComponent,
	],
})
export class ShopperNotesComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() title = 'Personal Shopper instructions';
	@Input() description = 'Leave special instructions on how we should pick this product here.';
	@Input() initialValue: string;

	@HostBinding('attr.data-ismodal')
	@Input()
	isModal = false;

	@Input() productName: string;
	@Input() productImageSrc: string;
	@Input() shopperNotesEndpoint: string;
	@Input() sku: string;

	@ViewChild('form', { static: true }) form: FormComponent;

	maxlength = 250;
	shopperNotesForm: FormGroup;
	buttonState: ButtonState = ButtonState.default;
	submitting: Boolean = false;
	private destroyed$: Subject<boolean> = new Subject();

	constructor(
		private fb: FormBuilder,
		private datalayerService: DatalayerService,
		private modalOverlayService: ModalOverlayService,
		private productService: ProductService,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		this.shopperNotesForm = this.fb.group({
			shopperNote: [''],
		});

		this.productService.state$.pipe(takeUntil(this.destroyed$)).subscribe((state) => {
			this.productName = state.product?.name || '';

			if (this.submitting) {
				if (!!state.error) {
					const invalidChars =
						state.error.error?.errors
							?.find((e: any) => e.field === 'ShopperNotes')
							?.message?.indexOf('Invalid character') >= 0 ?? false;
					this.shopperNotesForm.controls['shopperNote']?.setErrors(
						invalidChars ? { invalidChars: true } : { serverError: true }
					);
					return;
				}

				if (this.isModal) {
					this.modalOverlayService.close();
					return;
				}

				this.submitting = false;
			}

			const shopperNote = state.shopperNote?.replace(/\\n/g, '\n');
			this.initialValue = shopperNote;

			this.shopperNotesForm.patchValue({
				shopperNote: shopperNote || '',
			});

			this.cdr.markForCheck();
		});

		this.productService.getShopperNotes(this.sku);
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	ngAfterViewInit(): void {
		this.form.submitted$.pipe(takeUntil(this.destroyed$)).subscribe((isSubmitted) => {
			if (isSubmitted) {
				this.buttonState = ButtonState.busy;
			}
		});
	}

	canSubmit(): boolean {
		const currentValue = this.shopperNotesForm.get('shopperNote')?.value?.trim();
		return (
			(!this.initialValue && !!currentValue) ||
			(!!this.initialValue && !currentValue) ||
			(this.initialValue && currentValue && this.initialValue !== currentValue)
		);
	}

	onSubmit(): void {
		if (this.canSubmit()) {
			this.submitting = true;

			this.productService.addOrUpdateShopperNotes({
				sku: this.sku,
				shopperNotes: (this.shopperNotesForm.get('shopperNote')?.value || '').trim().replace(/(\r?\n)/g, '\\n'),
			});

			this.datalayerService.trackNotificationEvent(NotificationType.Modal, 'shopper notes', 'save notes');
		}
	}
}
